/* eslint-disable react/prop-types */
/* eslint-disable react/function-component-definition */

import { useMemo } from "react";

// Common Components
import Author from "components/Table/Author";
import MDBox from "components/MDBox";

// Constants
import Constants, { Colors, defaultData, PrecisionUtils } from "utils/Constants";

// 3rd party libraries
import moment from "moment";

const noop = () => {};

export default function ProgressSummaryData(
  data,
  reportData,
  totalCompletionPercentage,
  handleLocationClick = noop
) {
  const handleCellBackground = (value) =>
    PrecisionUtils.getCompletionBackgroundColor(value, Colors);

  const completionPercentage = (isRequired, completion) =>
    PrecisionUtils.formatCompletionPercentage(isRequired, completion);

  const computedData = useMemo(() => {
    const reportTitleList = [];
    const reportValueList = [];
    const reportCompletionList = [];
    let totalReportColSpan = 0;

    const scopeList = data
      ? data.map((scope) => {
          scope?.reports?.forEach((report) => {
            reportTitleList.push({
              Header: `${report?.title}`,
              align: "center",
              colSpan: 1,
              key: report[Constants.MONGOOSE_ID],
            });
            reportValueList.push({
              Header: `${report?.weightage?.toFixed(2)}%`,
              align: "right",
              colSpan: 1,
              type: report?.type,
              key: report[Constants.MONGOOSE_ID],
              accessor: report[Constants.MONGOOSE_ID],
            });
            reportCompletionList.push({
              Footer: `${report?.reportCompletion?.toFixed(2) || 0}%`,
              align: "right",
              colSpan: 1,
              key: report[Constants.MONGOOSE_ID],
            });
          });
          totalReportColSpan += scope?.reports?.length || 0;
          return {
            Header: scope?.name,
            align: "center",
            colSpan: scope?.reports?.length,
            key: scope[Constants.MONGOOSE_ID],
          };
        })
      : [];

    const scopeHeader = scopeList.length
      ? [
          { Header: "", colSpan: 2, key: "scope" },
          ...scopeList,
          { Header: "", colSpan: 1, key: "locationCompletionScope" },
        ]
      : [];
    const reportTitleHeader = reportTitleList.length
      ? [
          { Header: "", colSpan: 2, key: "reportTitle" },
          ...reportTitleList,
          { Header: "", colSpan: 1, key: "locationCompletionTitle" },
        ]
      : [];

    const reportValueHeader = [
      {
        Header: "Location",
        accessor: "location",
        align: "left",
        width: "16%",
        type: "location",
        key: "location",
      },
      {
        Header: "Asset",
        accessor: "asset",
        align: "left",
        width: "14%",
        type: "asset",
        key: "asset",
      },
      ...reportValueList,
      {
        Header: "Location Completion",
        accessor: "locationCompletion",
        align: "right",
        width: "12%",
        type: "locationCompletion",
        key: "locationCompletion",
      },
    ];

    const reportCompletionFooter = [
      { Footer: "Completion", align: "left", colSpan: 2, key: "completion" },
      ...reportCompletionList,
      { Footer: "", align: "right", colSpan: 1, key: "locationCompletionBlank" },
    ];

    const reportTotalFooter = [
      { Footer: "", align: "left", colSpan: totalReportColSpan + 1, key: "completion" },
      { Footer: "Total Completion", align: "left", colSpan: 1, key: "totalCompletion" },
      {
        Footer: `${PrecisionUtils.getCompletionDisplayValue(totalCompletionPercentage)}%`,
        align: "right",
        colSpan: 1,
        key: "totalPercentage",
      },
    ];

    const rows = reportData
      ? reportData?.map((report) => ({
          key: report[Constants.MONGOOSE_ID],
          rowSpan: report?.assets?.length || 1,
          colSpan: 1,
          align: "left",
          location: (
            <MDBox
              sx={{
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                width: "max-content",
                maxWidth: "250px",
              }}
              onClick={() => {
                handleLocationClick(report[Constants.MONGOOSE_ID], report?.locations);
              }}
            >
              <Author
                name={report?.locations || ""}
                style={{
                  textTransform: "none",
                  whiteSpace: "normal",
                  overflowWrap: "anywhere",
                  overflow: "visible",
                }}
              />
            </MDBox>
          ),
          assets: report?.assets?.map((asset) => ({
            title: (
              <Author
                maxContent={defaultData.MEDIUM_CONTENT_LENGTH_2}
                name={`${asset?.title || ""}`}
                style={{ width: "max-content", maxWidth: "190px", textTransform: "none" }}
              />
            ),
            key: asset[Constants.MONGOOSE_ID],
          })),
          reports: report?.reportList?.map((item) => ({
            key: item[Constants.MONGOOSE_ID],
            type: item?.type,
            rowSpan: item?.type === "location" ? report?.assets?.length || 1 : 1,
            colSpan: 1,
            align: "right",
            reportList: item?.reportList?.map((ele) => ({
              completion: (
                <MDBox sx={{ width: "100%", display: "flex", justifyContent: "flex-end" }}>
                  <MDBox sx={{ width: "max-content", textAlign: "right" }}>
                    {!PrecisionUtils.isCompleted(ele?.completion) ? (
                      <Author
                        style={{ textTransform: "none" }}
                        name={completionPercentage(ele?.isRequired, ele?.completion)}
                      />
                    ) : (
                      <Author
                        name={moment(ele?.completedDate).format(
                          ele.status.some((status) =>
                            [Constants.CHECKED, Constants.CLOSED].includes(status)
                          )
                            ? defaultData.WEB_DATE_FORMAT
                            : completionPercentage(ele?.isRequired, ele?.completion)
                        )}
                        style={{ textTransform: "none" }}
                      />
                    )}
                  </MDBox>
                </MDBox>
              ),
              isRequired: ele?.isRequired,
              bgColor: ele?.isRequired ? handleCellBackground(ele?.completion) : Colors.GREY1,
            })),
          })),
          locationCompletion: {
            value: `${PrecisionUtils.getCompletionDisplayValue(report?.locationCompletion)}%`,
            bgColor: handleCellBackground(report?.locationCompletion),
            align: "right",
          },
        }))
      : [];

    return {
      columns: reportValueHeader,
      scopeHeader,
      reportTitleHeader,
      footerList: [reportCompletionFooter, reportTotalFooter],
      rows,
    };
  }, [data, reportData, totalCompletionPercentage, handleLocationClick]);

  const { columns, scopeHeader, reportTitleHeader, footerList, rows } = computedData;

  const extraHeadersList = [scopeHeader, reportTitleHeader].filter((header) => header.length);

  return {
    columns,
    extraHeadersList,
    footerList,
    rows,
  };
}
